/**
 * Edit Client Page
 *
 * This page provides a comprehensive form for editing existing clients including:
 * - Client information (name, contact details, subscription tier)
 * - Form validation and error handling
 * - Success feedback and navigation
 */

import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { ActivQMSLoader } from "../../components/ui/activqms-loader";
import { SlackConfiguration } from "../../components/slack/slack-configuration";
import { SMSConfigurationComponent } from "../../components/sms/sms-configuration";
import { useGlobalClient } from "../../hooks/use-global-client";
import {
  GET_CLIENT,
  UPDATE_CLIENT,
  LIST_CLIENTS,
  ADD_CLIENT_ADMIN,
  REMOVE_CLIENT_ADMIN,
  UpdateClientInput,
  AddClientAdminInput,
  RemoveClientAdminInput,
  Client,
  ClientAdmin,
} from "../../services/client-api";

interface EditClientFormData {
  clientName: string;
  contactEmail: string;
  contactPhone: string;
  subscriptionTier: string;
}

interface AddAdminFormData {
  firstName: string;
  lastName: string;
  email: string;
}

export function EditClientPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { selectedClient } = useGlobalClient();
  const [formData, setFormData] = useState<EditClientFormData>({
    clientName: "",
    contactEmail: "",
    contactPhone: "",
    subscriptionTier: "BASIC",
  });
  const [errors, setErrors] = useState<Partial<EditClientFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionInProgress = useRef(false);

  // Admin user management state
  const [showAddAdminForm, setShowAddAdminForm] = useState(false);
  const [addAdminFormData, setAddAdminFormData] = useState<AddAdminFormData>({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [addAdminErrors, setAddAdminErrors] = useState<
    Partial<AddAdminFormData>
  >({});
  const [isAddingAdmin, setIsAddingAdmin] = useState(false);
  const [removingAdminId, setRemovingAdminId] = useState<string | null>(null);

  // Fetch existing client data
  const {
    data: clientData,
    loading: clientLoading,
    error: clientError,
  } = useQuery(GET_CLIENT, {
    variables: { clientId: id },
    skip: !id,
    onCompleted: (data) => {
      if (data?.getClient) {
        const client = data.getClient;
        setFormData({
          clientName: client.clientName || "",
          contactEmail: client.contactEmail || "",
          contactPhone: client.contactPhone || "",
          subscriptionTier: client.subscriptionTier || "BASIC",
        });
      }
    },
  });

  const [updateClient] = useMutation(UPDATE_CLIENT, {
    refetchQueries: [
      { query: LIST_CLIENTS },
      { query: GET_CLIENT, variables: { clientId: id } },
    ],
    onCompleted: (data) => {
      console.log("Client updated successfully:", data);
      setIsSubmitting(false);
      submissionInProgress.current = false;
      navigate(`/clients/${id}`, {
        state: {
          message: `Client "${data.updateClient.clientName}" updated successfully!`,
          type: "success",
        },
      });
    },
    onError: (error) => {
      console.error("Error updating client:", error);
      setIsSubmitting(false);
      submissionInProgress.current = false;
    },
  });

  const [addClientAdmin] = useMutation(ADD_CLIENT_ADMIN, {
    update: (cache, { data }) => {
      if (data?.addClientAdmin && id) {
        // Read the current client data from cache
        const existingClient = cache.readQuery({
          query: GET_CLIENT,
          variables: { clientId: id },
        }) as { getClient: Client } | null;

        if (existingClient?.getClient) {
          // Add the new admin user to the existing admin users list
          const updatedClient = {
            ...existingClient.getClient,
            adminUsers: [
              ...existingClient.getClient.adminUsers,
              data.addClientAdmin,
            ],
          };

          // Write the updated client data back to cache
          cache.writeQuery({
            query: GET_CLIENT,
            variables: { clientId: id },
            data: {
              getClient: updatedClient,
            },
          });
        }
      }
    },
    onCompleted: (data) => {
      console.log("Admin user added successfully:", data);
      setIsAddingAdmin(false);
      setShowAddAdminForm(false);
      setAddAdminFormData({ firstName: "", lastName: "", email: "" });
      setAddAdminErrors({});
    },
    onError: (error) => {
      console.error("Error adding admin user:", error);
      setIsAddingAdmin(false);
    },
  });

  const [removeClientAdmin] = useMutation(REMOVE_CLIENT_ADMIN, {
    update: (cache, { data }, { variables }) => {
      if (data?.removeClientAdmin && variables?.input?.userId && id) {
        // Read the current client data from cache
        const existingClient = cache.readQuery({
          query: GET_CLIENT,
          variables: { clientId: id },
        }) as { getClient: Client } | null;

        if (existingClient?.getClient) {
          // Remove the admin user from the existing admin users list
          const updatedClient = {
            ...existingClient.getClient,
            adminUsers: existingClient.getClient.adminUsers.filter(
              (admin: ClientAdmin) => admin.id !== variables.input.userId
            ),
          };

          // Write the updated client data back to cache
          cache.writeQuery({
            query: GET_CLIENT,
            variables: { clientId: id },
            data: {
              getClient: updatedClient,
            },
          });
        }
      }
    },
    onCompleted: () => {
      console.log("Admin user removed successfully");
      setRemovingAdminId(null);
    },
    onError: (error) => {
      console.error("Error removing admin user:", error);
      setRemovingAdminId(null);
    },
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof EditClientFormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<EditClientFormData> = {};

    if (!formData.clientName.trim()) {
      newErrors.clientName = "Client name is required";
    }

    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = "Contact email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = "Please enter a valid email address";
    }

    if (!formData.subscriptionTier) {
      newErrors.subscriptionTier = "Subscription tier is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (submissionInProgress.current || isSubmitting) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    if (!id) {
      console.error("No client ID provided");
      return;
    }

    setIsSubmitting(true);
    submissionInProgress.current = true;

    const updateInput: UpdateClientInput = {
      clientId: id,
      clientName: formData.clientName.trim(),
      contactEmail: formData.contactEmail.trim(),
      contactPhone: formData.contactPhone.trim() || undefined,
      subscriptionTier: formData.subscriptionTier as any,
    };

    try {
      await updateClient({
        variables: {
          input: updateInput,
        },
      });
    } catch (error) {
      console.error("Error in form submission:", error);
      setIsSubmitting(false);
      submissionInProgress.current = false;
    }
  };

  // Admin user management handlers
  const handleAddAdminInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setAddAdminFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (addAdminErrors[name as keyof AddAdminFormData]) {
      setAddAdminErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateAddAdminForm = (): boolean => {
    const newErrors: Partial<AddAdminFormData> = {};

    if (!addAdminFormData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!addAdminFormData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!addAdminFormData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addAdminFormData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setAddAdminErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddAdmin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isAddingAdmin || !validateAddAdminForm() || !id) {
      return;
    }

    setIsAddingAdmin(true);

    const addAdminInput: AddClientAdminInput = {
      clientId: id,
      firstName: addAdminFormData.firstName.trim(),
      lastName: addAdminFormData.lastName.trim(),
      email: addAdminFormData.email.trim(),
    };

    try {
      await addClientAdmin({
        variables: {
          input: addAdminInput,
        },
      });
    } catch (error) {
      console.error("Error adding admin user:", error);
      setIsAddingAdmin(false);
    }
  };

  const handleRemoveAdmin = async (userId: string) => {
    if (removingAdminId || !id) {
      return;
    }

    setRemovingAdminId(userId);

    const removeAdminInput: RemoveClientAdminInput = {
      clientId: id,
      userId: userId,
    };

    try {
      await removeClientAdmin({
        variables: {
          input: removeAdminInput,
        },
      });
    } catch (error) {
      console.error("Error removing admin user:", error);
      setRemovingAdminId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusStyles = {
      ACTIVE: "bg-green-100 text-green-800",
      INACTIVE: "bg-gray-100 text-gray-800",
      PENDING: "bg-yellow-100 text-yellow-800",
      SUSPENDED: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          statusStyles[status as keyof typeof statusStyles] ||
          statusStyles.PENDING
        }`}
      >
        {status}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (clientLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ActivQMSLoader size="lg" />
      </div>
    );
  }

  if (clientError || !clientData?.getClient) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/clients")}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Back to Clients
          </button>
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Client Not Found</h1>
          <p className="mt-2 text-gray-600">
            The client you're trying to edit doesn't exist or you don't have
            permission to access it.
          </p>
        </div>
      </div>
    );
  }

  const client = clientData.getClient;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate(`/clients/${id}`)}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-1" />
          Back to Client Details
        </button>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-gray-900">Edit Client</h1>
        <p className="mt-1 text-sm text-gray-600">
          Update client information and settings
        </p>
      </div>

      {/* Client Information Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Client Information */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Client Information
            </h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {/* Client Name */}
              <div>
                <label
                  htmlFor="clientName"
                  className="block text-sm font-medium text-gray-700"
                >
                  Client Name *
                </label>
                <input
                  type="text"
                  name="clientName"
                  id="clientName"
                  value={formData.clientName}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors.clientName ? "border-red-300" : ""
                  }`}
                  placeholder="Enter client name"
                />
                {errors.clientName && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.clientName}
                  </p>
                )}
              </div>

              {/* Subscription Tier */}
              <div>
                <label
                  htmlFor="subscriptionTier"
                  className="block text-sm font-medium text-gray-700"
                >
                  Subscription Tier *
                </label>
                <select
                  name="subscriptionTier"
                  id="subscriptionTier"
                  value={formData.subscriptionTier}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors.subscriptionTier ? "border-red-300" : ""
                  }`}
                >
                  <option value="FREE">Free</option>
                  <option value="BASIC">Basic</option>
                  <option value="PROFESSIONAL">Professional</option>
                  <option value="ENTERPRISE">Enterprise</option>
                </select>
                {errors.subscriptionTier && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.subscriptionTier}
                  </p>
                )}
              </div>

              {/* Contact Email */}
              <div>
                <label
                  htmlFor="contactEmail"
                  className="block text-sm font-medium text-gray-700"
                >
                  Contact Email *
                </label>
                <input
                  type="email"
                  name="contactEmail"
                  id="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors.contactEmail ? "border-red-300" : ""
                  }`}
                  placeholder="contact@example.com"
                />
                {errors.contactEmail && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.contactEmail}
                  </p>
                )}
              </div>

              {/* Contact Phone */}
              <div>
                <label
                  htmlFor="contactPhone"
                  className="block text-sm font-medium text-gray-700"
                >
                  Contact Phone
                </label>
                <input
                  type="tel"
                  name="contactPhone"
                  id="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate(`/clients/${id}`)}
            className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <ActivQMSLoader size="sm" className="mr-2" />
                Updating...
              </>
            ) : (
              "Update Client"
            )}
          </button>
        </div>
      </form>

      {/* Admin Users Management - Separate from main form */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
              <UserGroupIcon className="h-5 w-5 mr-2" />
              Admin Users
            </h3>
            <button
              type="button"
              onClick={() => setShowAddAdminForm(true)}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Admin User
            </button>
          </div>

          {/* Add Admin Form */}
          {showAddAdminForm && (
            <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <h4 className="text-md font-medium text-gray-900 mb-3">
                Add New Admin User
              </h4>
              <form onSubmit={handleAddAdmin} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium text-gray-700"
                    >
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      id="firstName"
                      value={addAdminFormData.firstName}
                      onChange={handleAddAdminInputChange}
                      className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                        addAdminErrors.firstName ? "border-red-300" : ""
                      }`}
                      placeholder="Enter first name"
                    />
                    {addAdminErrors.firstName && (
                      <p className="mt-2 text-sm text-red-600">
                        {addAdminErrors.firstName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      id="lastName"
                      value={addAdminFormData.lastName}
                      onChange={handleAddAdminInputChange}
                      className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                        addAdminErrors.lastName ? "border-red-300" : ""
                      }`}
                      placeholder="Enter last name"
                    />
                    {addAdminErrors.lastName && (
                      <p className="mt-2 text-sm text-red-600">
                        {addAdminErrors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={addAdminFormData.email}
                    onChange={handleAddAdminInputChange}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      addAdminErrors.email ? "border-red-300" : ""
                    }`}
                    placeholder="admin@example.com"
                  />
                  {addAdminErrors.email && (
                    <p className="mt-2 text-sm text-red-600">
                      {addAdminErrors.email}
                    </p>
                  )}
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddAdminForm(false);
                      setAddAdminFormData({
                        firstName: "",
                        lastName: "",
                        email: "",
                      });
                      setAddAdminErrors({});
                    }}
                    className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    disabled={isAddingAdmin}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isAddingAdmin}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAddingAdmin ? (
                      <>
                        <ActivQMSLoader size="sm" className="mr-2" />
                        Adding...
                      </>
                    ) : (
                      "Add Admin User"
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Admin Users List */}
          {client.adminUsers && client.adminUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {client.adminUsers.map((user: ClientAdmin) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {user.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(user.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.lastLoginAt
                          ? formatDate(user.lastLoginAt)
                          : "Never"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleRemoveAdmin(user.id)}
                          disabled={removingAdminId === user.id}
                          className="inline-flex items-center text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {removingAdminId === user.id ? (
                            <ActivQMSLoader size="sm" className="mr-1" />
                          ) : (
                            <TrashIcon className="h-4 w-4 mr-1" />
                          )}
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="text-sm text-gray-500">No admin users found.</div>
            </div>
          )}
        </div>
      </div>

      {/* Slack Integration Configuration */}
      {clientData?.getClient && (
        <SlackConfiguration
          clientId={clientData.getClient.clientId}
          onConfigurationChange={() => {
            // Optionally refresh client data or show success message
            console.log("Slack configuration updated");
          }}
        />
      )}

      {/* SMS Configuration */}
      {clientData?.getClient ? (
        <div className="space-y-2">
          <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-md p-3">
            <strong>Configuring SMS for:</strong>{" "}
            {clientData.getClient.clientName} ({clientData.getClient.clientId})
          </div>
          <SMSConfigurationComponent
            tenantId={clientData.getClient.clientId}
            onConfigurationChange={(config) => {
              // Optionally refresh client data or show success message
              console.log("SMS configuration updated:", config);
            }}
          />
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <p className="text-yellow-800">Loading client data...</p>
        </div>
      )}
    </div>
  );
}
